import { useState, useEffect, useCallback } from 'react';
import { getNormalizedViewportSize, BREAKPOINTS } from '../utils/responsive';

/**
 * Custom hook for tracking window size changes with zoom awareness
 * Useful for responsive design and mobile optimizations in Electron apps
 * 
 * @param {boolean} useNormalized - Whether to use zoom-normalized dimensions
 * @returns {object} - { width, height, normalizedWidth, normalizedHeight, zoomLevel, scaleFactor, isMobile, isTablet, isDesktop }
 */
export const useWindowSize = (useNormalized = true) => {
    const [windowSize, setWindowSize] = useState({
        width: undefined,
        height: undefined,
        normalizedWidth: undefined,
        normalizedHeight: undefined,
        zoomLevel: 0,
        scaleFactor: 1,
    });

    const updateWindowSize = useCallback(() => {
        const normalized = getNormalizedViewportSize();
        
        setWindowSize({
            width: window.innerWidth,
            height: window.innerHeight,
            normalizedWidth: normalized.width,
            normalizedHeight: normalized.height,
            zoomLevel: normalized.zoomLevel,
            scaleFactor: normalized.scaleFactor,
        });
    }, []);

    useEffect(() => {
        // Add event listener for window resize
        window.addEventListener('resize', updateWindowSize);
        
        // Listen for zoom changes in Electron
        if (typeof window !== 'undefined' && window.require) {
            try {
                const { ipcRenderer } = window.require('electron');
                const handleZoomChange = () => {
                    // Use setTimeout to ensure zoom level is updated
                    setTimeout(updateWindowSize, 50);
                };
                ipcRenderer.on('zoom-changed', handleZoomChange);
                
                // Cleanup function for IPC listener
                const cleanup = () => {
                    ipcRenderer.removeListener('zoom-changed', handleZoomChange);
                };
                
                // Store cleanup function for later use
                window.addEventListener('beforeunload', cleanup);
                
                // Initial call
                updateWindowSize();
                
                return () => {
                    window.removeEventListener('resize', updateWindowSize);
                    window.removeEventListener('beforeunload', cleanup);
                    cleanup();
                };
            } catch (error) {
                console.log('Not in Electron environment for zoom detection');
            }
        }
        
        // Call handler right away so state gets updated with initial window size
        updateWindowSize();

        // Remove event listener on cleanup
        return () => window.removeEventListener('resize', updateWindowSize);
    }, [updateWindowSize]);

    // Use normalized or actual dimensions based on preference
    const effectiveWidth = useNormalized ? windowSize.normalizedWidth : windowSize.width;
    const effectiveHeight = useNormalized ? windowSize.normalizedHeight : windowSize.height;

    return {
        ...windowSize,
        effectiveWidth,
        effectiveHeight,
        isMobile: effectiveWidth && effectiveWidth < BREAKPOINTS.md,
        isTablet: effectiveWidth && effectiveWidth >= BREAKPOINTS.md && effectiveWidth < BREAKPOINTS.lg,
        isDesktop: effectiveWidth && effectiveWidth >= BREAKPOINTS.lg,
    };
};

/**
 * Custom hook for tracking element visibility
 * Useful for lazy loading and performance optimization
 * 
 * @param {object} options - Intersection Observer options
 * @returns {[ref, isVisible]} - ref to attach to element and visibility state
 */
export const useIntersectionObserver = (options = {}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [ref, setRef] = useState(null);

    useEffect(() => {
        if (!ref) return;

        const observer = new IntersectionObserver(([entry]) => {
            setIsVisible(entry.isIntersecting);
        }, {
            threshold: 0.1,
            ...options
        });

        observer.observe(ref);

        return () => {
            if (ref) {
                observer.unobserve(ref);
            }
        };
    }, [ref, options]);

    return [setRef, isVisible];
};